import { useState, useEffect } from 'react'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { getLocalStatus } from '@/api/profiles'

export type ConnectionState = 'checking' | 'connected' | 'error' | 'noDB' | 'noProxy'

export interface ConnectionStatus {
  state: ConnectionState
  profileName: string | null
  error: string | null
  refresh: () => void
}

export function useConnectionStatus(): ConnectionStatus {
  const { proxyUrl, proxyToken, isElectron } = useProxyConfig()
  const [tick, setTick] = useState(0)
  const [status, setStatus] = useState<Omit<ConnectionStatus, 'refresh'>>({
    state: isElectron ? 'checking' : 'noProxy',
    profileName: null,
    error: null,
  })

  useEffect(() => {
    if (!isElectron || !proxyUrl) {
      setStatus({ state: 'noProxy', profileName: null, error: null })
      return
    }

    let cancelled = false

    async function check() {
      setStatus({ state: 'checking', profileName: null, error: null })
      try {
        const configRes = await fetch(`${proxyUrl}/local/config`, {
          headers: proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {},
          signal: AbortSignal.timeout(3000),
        })
        const config = await configRes.json() as { hasDatabaseUrl: boolean }
        if (cancelled) return

        if (!config.hasDatabaseUrl) {
          setStatus({ state: 'noDB', profileName: null, error: null })
          return
        }

        const result = await getLocalStatus(proxyUrl!, proxyToken)
        if (cancelled) return

        if (result.connected) {
          setStatus({ state: 'connected', profileName: result.profileName ?? null, error: null })
        } else {
          setStatus({ state: 'error', profileName: result.profileName ?? null, error: result.error ?? 'Sin conexión' })
        }
      } catch {
        if (!cancelled) {
          setStatus({ state: 'error', profileName: null, error: 'No se pudo verificar la conexión' })
        }
      }
    }

    check()
    return () => { cancelled = true }
  }, [isElectron, proxyUrl, proxyToken, tick])

  return { ...status, refresh: () => setTick(t => t + 1) }
}
